import type { LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import { convertProductToImpressionCart, trackCartChange } from "~/app/analytics/ga/ga";
import type { RootState } from "~/app/store";
import { groupByFn } from "~/common/utils/reducers";
import { resolveCartDescriptor, quotePackCodeFromBuilders, type CartPnaPair } from "../../pna/cartPna";
import { getPricePerPiece } from "~/features/cart/cart/utils/lineItemPriceUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

/**
 * Descriptors for one product's line items, keyed by price reference (A07). With
 * `resultsByPriceReference` (awaited `loadCartPna` pairs) it projects the returned values;
 * otherwise it resolves descriptors synchronously from the canonical query cache — the
 * equivalent of the legacy `state.products` dictionary read.
 */
export function getDesciptorsForLineItems(
  state: RootState,
  productCode: string,
  lineItems: LineItemBuilder[],
  resultsByPriceReference?: Dict<CartPnaPair>,
): Dict<ProductDescriptor> {
  const packCode = quotePackCodeFromBuilders(lineItems);
  const priceReferences = Object.keys(groupByFn(lineItems, (item) => item.get("priceReference")));
  return priceReferences.reduce((map, priceReference) => {
    map[priceReference] = resolveCartDescriptor(
      state,
      productCode,
      priceReference,
      resultsByPriceReference?.[priceReference],
      packCode,
    );
    return map;
  }, {} as Dict<ProductDescriptor>);
}

export function trackCartUpdate(editedLineItem: LineItemBuilder, productDescriptor: ProductDescriptor) {
  if (!editedLineItem.hasUpdatesForSending()) {
    return;
  }
  const { base, availability } = productDescriptor;

  const oldLineItem = editedLineItem.getOriginal();
  const oldImpression = convertProductToImpressionCart(base, oldLineItem, availability, getPricePerPiece(oldLineItem));

  const newLineItem = editedLineItem.buildForSending()!;
  const newImpression = convertProductToImpressionCart(base, newLineItem, availability, getPricePerPiece(newLineItem));
  trackCartChange(oldImpression, newImpression);
}
